/**
 * Automation templates: global catalog of pre-built automation recipes.
 * Installers can enable a template to create an automation_rule from its defaults.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import type { DagDefinition } from '@/lib/dag-types';

const TEMPLATES_KEY = 'automation-templates';
const RULES_KEY = 'automation-rules';

export interface AutomationTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  default_trigger_type: string;
  default_trigger_config: Record<string, unknown>;
  default_action_type: string;
  default_action_config: Record<string, unknown>;
  default_cooldown_minutes: number;
  icon: string | null;
  sort_order: number;
  is_recommended: boolean;
  settings_changes: Array<{ field: string; value: unknown }> | null;
  supported_providers: string[] | null;
  risk_level: string | null;
  coming_soon: boolean;
  is_dag: boolean;
  dag_definition: DagDefinition | null;
  created_at: string;
}

async function fetchTemplates(): Promise<AutomationTemplate[]> {
  const { data, error } = await supabase
    .from('automation_templates')
    .select('*')
    .order('sort_order');
  if (error) throw error;
  return (data ?? []) as AutomationTemplate[];
}

export function useAutomationTemplates() {
  const query = useQuery({
    queryKey: [TEMPLATES_KEY],
    queryFn: fetchTemplates,
  });
  return {
    templates: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
  };
}

/**
 * Enable a template: creates an automation_rule from the template defaults.
 */
export function useEnableAutomationTemplate() {
  const { organization } = useApp();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (template: AutomationTemplate) => {
      if (!organization?.id) throw new Error('No organization');
      const { data, error } = await supabase
        .from('automation_rules')
        .insert({
          organization_id: organization.id,
          name: template.name,
          description: template.description,
          is_enabled: template.is_dag ? false : true,
          trigger_type: template.default_trigger_type,
          trigger_config: template.default_trigger_config,
          scope_type: 'all',
          scope_property_ids: [],
          scope_tags: [],
          schedule_type: 'always',
          action_type: template.default_action_type,
          action_config: template.default_action_config,
          cooldown_minutes: template.default_cooldown_minutes,
          is_dag: template.is_dag ?? false,
          dag_definition: template.dag_definition ?? null,
          template_id: template.id,
          updated_at: new Date().toISOString(),
        })
        .select('id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [RULES_KEY] });
    },
  });
}
